import React from 'react';
import { observer } from 'mobx-react';
import { CartCalculationLineItem } from './CartCalculationLineItem';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { store } from 'Models/Store';
import { getMessageByKey } from 'Stores/LanguageStore';
import { IPriceLineItem } from 'Services/Api/_HumanWritten/PricingService/PricingResult';

export interface AddedFeeLinesProps {
	fees: IPriceLineItem[];
	onRemoveFee?: (id: string) => void;
}

/** A set of identical fee line items (same name + rate) collapsed into one display row. */
interface GroupedFee {
	/** Fee name without the trailing ":" (e.g. "Payment Processing Fee"). */
	name: string;
	/** Per-instance rate as supplied by the backend — "4%" or "$4". May be empty. */
	rate: string;
	/** Summed dollar amount across every instance in the group. */
	total: number;
	/** Ids of every underlying fee — removing the row removes all of them. */
	ids: string[];
	/** Only offer removal when every instance in the group is removable. */
	removable: boolean;
}

/**
 * Collapses identical fees into a single row. A per-booking fee charged on several
 * bookings (one FeeEntity each, same name + rate) would otherwise render as one
 * duplicated line per booking. We group by name + rate, sum the amounts, and show
 * the per-booking rate (e.g. "4% per booking (×3)") so the total stays explainable.
 */
function groupFees(fees: IPriceLineItem[]): GroupedFee[] {
	const groups = new Map<string, GroupedFee>();
	fees.forEach(fee => {
		const name = fee.description.replace(':', '').trim();
		const rate = fee.subDescription ?? '';
		const key = `${name}__${rate}`;
		const existing = groups.get(key);
		if (existing) {
			existing.total += fee.price;
			if (isNotNullOrUndefined(fee.id)) existing.ids.push(fee.id);
			existing.removable = existing.removable && fee.removable;
		} else {
			groups.set(key, {
				name,
				rate,
				total: fee.price,
				ids: isNotNullOrUndefined(fee.id) ? [fee.id] : [],
				removable: fee.removable,
			});
		}
	});
	return Array.from(groups.values());
}

function AddedFeeLines({ fees, onRemoveFee }: AddedFeeLinesProps) {
	const feesToDisplay = fees?.filter(x => x.price > 0) ?? [];
	// remove-fee-from-transaction carries [RequirePermission(ManageServiceFees)].
	const canRemoveServiceFee = store.can('ManageServiceFees');
	const groupedFees = groupFees(feesToDisplay);
	// Configurable wording for the per-booking fee annotation (e.g. "$4 per booking (×3)").
	const perBookingLabel = getMessageByKey('cart__fee-per-booking__label', 'per booking');

	return (
		<>
			{groupedFees.map(group => {
				const count = group.ids.length;
				// Only annotate "per booking (×N)" once a fee spans multiple bookings;
				// a single instance (incl. transaction-level fees) just shows its rate.
				let description: string | undefined;
				if (group.rate) {
					description = count > 1 ? `${group.rate} ${perBookingLabel} (×${count})` : group.rate;
				}
				return (
					<CartCalculationLineItem
						key={group.ids[0] ?? group.name}
						keyToUse={group.ids[0] ?? group.name}
						lineItemDisplayName={group.name}
						lineItemDescription={description}
						lineItemPrice={formatPriceDisplay(group.total, true)}
						className="service-fee__line-item"
						hasRemovalButton={isNotNullOrUndefined(onRemoveFee) && group.removable && canRemoveServiceFee}
						removalOnClick={() => {
							if (isNotNullOrUndefined(onRemoveFee)) {
								group.ids.forEach(id => onRemoveFee(id));
							}
						}}
					/>
				);
			})}
		</>
	);
}

export default observer(AddedFeeLines);
